'use client';

import { useState } from 'react';

type Props = {
  email: string;
  className?: string;
  children: React.ReactNode;
};

// Click-to-copy email with a ripple confirmation that expands from the click
// point and a moss-green "COPIED" chip that slides in. Falls back to a
// mailto navigation if the clipboard API throws.
export function CopyEmailButton({ email, className, children }: Props) {
  const [ripples, setRipples] = useState<{ id: number; x: number; y: number }[]>([]);
  const [copied, setCopied] = useState(false);

  async function onClick(e: React.MouseEvent<HTMLButtonElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = Date.now();
    setRipples((r) => [...r, { id, x, y }]);
    setTimeout(() => setRipples((r) => r.filter((rp) => rp.id !== id)), 900);

    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.location.href = `mailto:${email}`;
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={className}
      aria-label={`Copy ${email} to clipboard`}
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: 'transparent',
        border: 'none',
        cursor: 'copy',
        font: 'inherit',
        color: 'inherit',
        padding: 0,
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      {children}
      <span
        aria-live="polite"
        style={{
          marginLeft: 8,
          fontSize: 10,
          letterSpacing: '0.12em',
          opacity: copied ? 1 : 0,
          transform: copied ? 'translateX(0)' : 'translateX(-6px)',
          transition: 'opacity 220ms var(--ease-calm), transform 220ms var(--ease-calm)',
          color: 'var(--moss)',
          fontFamily: 'var(--font-mono)',
          fontWeight: 700,
          whiteSpace: 'nowrap',
        }}
      >
        {copied ? '✓ COPIED' : ''}
      </span>
      {ripples.map((r) => (
        <span
          key={r.id}
          aria-hidden
          style={{
            position: 'absolute',
            left: r.x,
            top: r.y,
            width: 4,
            height: 4,
            marginLeft: -2,
            marginTop: -2,
            borderRadius: '50%',
            background: 'color-mix(in srgb, var(--accent) 60%, transparent)',
            animation: 'copy-ripple 900ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
            pointerEvents: 'none',
          }}
        />
      ))}
    </button>
  );
}
